// src/features/dashboard/hooks/Employee/useEmployeeCompliance.ts

import {
  useCallback,
  useEffect,
  useState,
} from "react";
import { useDashboardDate } from "../../context/DashboardDateContext";
import { formatApiDate } from "../../utils/date.utils";

import { ENV } from "../../../../config/env";

import {
  employeeComplianceMock,
} from "../../mocks/Employee/employeeCompliance.mock";

import {
  getComplianceKPI,
  getComplianceLoginTrend,
  getComplianceHoursRequirement,
  getCompliancePolicy,
  getComplianceMonthlyScorecard,
} from "../../services/Employee/employeeCompliance.service";

import {
  BreakChartItem,
  BreakTableItem,
  ComplianceCardItem,
  ComplianceHeaderData,
  ComplianceHeatmapRow,
  HoursChartItem,
  HoursTableItem,
  LoginChartItem,
  LoginTableItem,
  PolicyChartItem,
  PolicyTableItem,
} from "../../types/Employee/employeeCompliance.types";

export interface EmployeeComplianceRequest {
  employeeCode: string;
  date: string;
}

const useEmployeeCompliance = () => {

  const { fromDate, toDate } = useDashboardDate();

  const [allowedWindow, setAllowedWindow] = useState("");

 const period =
  fromDate.getMonth() === toDate.getMonth() &&
  fromDate.getFullYear() === toDate.getFullYear()
    ? `${fromDate.toLocaleString("default", {
        month: "long",
      })} ${fromDate.getFullYear()}`
    : `${fromDate.toLocaleString("default", {
        month: "long",
      })}–${toDate.toLocaleString("default", {
        month: "long",
      })} ${toDate.getFullYear()}`;


  
  const [header, setHeader] =
    useState<ComplianceHeaderData | null>(null);

  const [cards, setCards] =
    useState<ComplianceCardItem[]>([]);

  const [loginChart, setLoginChart] =
    useState<LoginChartItem[]>([]);

  const [loginTable, setLoginTable] =
    useState<LoginTableItem[]>([]);

  const [breakChart, setBreakChart] =
    useState<BreakChartItem[]>([]);

  const [breakTable, setBreakTable] =
    useState<BreakTableItem[]>([]);

  const [hoursChart, setHoursChart] =
    useState<HoursChartItem[]>([]);

  const [hoursTable, setHoursTable] =
    useState<HoursTableItem[]>([]);

  const [policyChart, setPolicyChart] =
    useState<PolicyChartItem[]>([]);

  const [policyTable, setPolicyTable] =
    useState<PolicyTableItem[]>([]);

  const [heatmapData, setHeatmapData] =
  useState({
    period: "",
    subtitle: "",
    rows: [] as ComplianceHeatmapRow[],
  });

  const [loading, setLoading] =
    useState(true);

  const [headerError, setHeaderError] = useState<string | null>(null);
const [loginError, setLoginError] = useState<string | null>(null);
const [hoursError, setHoursError] = useState<string | null>(null);
const [policyError, setPolicyError] = useState<string | null>(null);
const [heatmapError, setHeatmapError] = useState<string | null>(null);

  const loadCompliance = useCallback(async () => {
   try {
  setLoading(true);

  setHeaderError(null);
  setLoginError(null);
  setHoursError(null);
  setPolicyError(null);
  setHeatmapError(null);

      // ---------------- MOCK ----------------

      if (ENV.USE_MOCK) {
        setHeader(employeeComplianceMock.header);

        setCards(employeeComplianceMock.cards);

        setLoginChart(employeeComplianceMock.loginChart);
        setLoginTable(employeeComplianceMock.loginTable);

        setBreakChart(employeeComplianceMock.breakChart);
        setBreakTable(employeeComplianceMock.breakTable);

        setHoursChart(employeeComplianceMock.hoursChart);
        setHoursTable(employeeComplianceMock.hoursTable);

        setPolicyChart(employeeComplianceMock.policyChart);
        setPolicyTable(employeeComplianceMock.policyTable);

  setHeatmapData({
  period: employeeComplianceMock.heatmapData.header.period,
  subtitle: employeeComplianceMock.heatmapData.header.subtitle,
  rows: employeeComplianceMock.heatmapData.rows,
});

        return;
      }

   // ---------------- API ----------------

const request: EmployeeComplianceRequest = {
  employeeCode: localStorage.getItem("employeeCode") ?? "",
  date: formatApiDate(fromDate),
};

// 👇 Add here
// console.log("loginChart:", loginChart);
// console.log("loginTable:", loginTable);
// console.log("hoursChart:", hoursChart);
// console.log("policyChart:", policyChart);


let kpi = null;
let loginTrend: any[] = [];
let hoursRequirement = null;
let policy: any = null;
let scorecard: any[] = [];

try {
  kpi = await getComplianceKPI(request);
}  catch (e: any) {
  if (e.response?.status === 404) {
  
 setHeader({
        title: "Compliance",
        gatesMet: 0,
        totalGates: 4,
        badge: {
            employeeName: localStorage.getItem("employeeName") ?? "",
            login: "--",
            breaks: "--",
            hours: "--",
            policy: "--",
            week: new Date().toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
            }),
        },
    });
  }
   setCards([
  {
    id: 1,
    title: "Login Compliance",
    status: "NoData",
    description: "--",
    subDescription: "--",
    iconBgColor: "#EEF2FF",
    iconColor: "#4F46E5",
    statusColor: "#98A2B3",
  },
  {
    id: 2,
    title: "Hours Compliance",
    status: "NoData",
    description: "00",
    subDescription: "--",
    iconBgColor: "#ECFDF3",
    iconColor: "#16A34A",
    statusColor: "#98A2B3",
  },
  {
    id: 3,
    title: "Policy Compliance",
    status: "NoData",
    description: "00",
    subDescription: "--",
    iconBgColor: "#FEF3C7",
    iconColor: "#F59E0B",
    statusColor: "#98A2B3",
  },
]);
  
}

try {
  loginTrend = await getComplianceLoginTrend(request);
} catch (e) {
  console.error("Login Error", e);
  setLoginError("Unable to load Login Trend");
}

try {
  hoursRequirement = await getComplianceHoursRequirement(request);
} catch (e) {
  console.error("Hours Error", e);
  setHoursError("Unable to load Hours Requirement");
}

try {
  policy = await getCompliancePolicy(request);
  console.log("Policy API:", policy);
} catch (e) {
  console.error("Policy Error", e);
  setPolicyError("Unable to load Policy");
}

try {
  scorecard = await getComplianceMonthlyScorecard(request);
  console.log("Scorecard API:", scorecard);
} catch (e) {
  console.error("Scorecard Error", e);
  setHeatmapError("Unable to load Monthly Scorecard");
}
            // ===========================
      // Header
      // ===========================

  if (kpi) {
  setHeader({
    title: "Compliance",
    gatesMet: kpi.metGates,
    totalGates: kpi.totalGates,
    badge: {
      employeeName: localStorage.getItem("employeeName") ?? "",
      login: kpi.loginComplianceStatus,
      breaks: "MET",
      hours: kpi.hoursComplianceStatus,
      policy: kpi.policyComplianceStatus,
      week: `${kpi.complianceScore}%`,
    },
  });

  setCards([
    {
      id: 1,
      title: "Login Gate",
      status:
          kpi.loginComplianceStatus === "MET"
            ? "Met"
            : "Failed",
      description: kpi.loginTime,
      subDescription: `Window ${kpi.allowedWindow}`,
      iconBgColor: "#EEF2FF",
      iconColor: "#4F46E5",
      statusColor:
        kpi.loginComplianceStatus === "MET"
          ? "#16A34A"
          : "#DC2626",
    },
    {
      id: 2,
      title: "Hours Requirement",
      status:
        kpi.hoursComplianceStatus === "MET"
          ? "Met"
          : "Failed",
      description: kpi.loggedHours,
      subDescription: "Logged Hours",
      iconBgColor: "#ECFDF3",
      iconColor: "#16A34A",
      statusColor:
        kpi.hoursComplianceStatus === "MET"
          ? "#16A34A"
          : "#DC2626",
    },
    {
      id: 3,
      title: "Policy Compliance",
      status:
         kpi.policyComplianceStatus === "MET"
          ? "Met"
          : "Failed",
      description: `${kpi.restrictedAppCount}`,
      subDescription: "Restricted Apps",
      iconBgColor: "#FEF3C7",
      iconColor: "#F59E0B",
      statusColor:
        kpi.policyComplianceStatus === "MET"
          ? "#16A34A"
          : "#F59E0B",
    },
  ]);
  }

      // ===========================
      // Login Chart
      // ===========================

   if (loginTrend.length) {
  setLoginChart(
    loginTrend.map((item) => ({
      day: item.dayLabel,
      loginTime: item.loginTime,
      buffer: 30,
    }))
  );

  setLoginTable(
    loginTrend.map((item) => ({
      day: item.dayLabel,
      loginTime: item.loginTime,
      window: item.allowedWindow,
      status:
        item.status === "MET"
          ? "Met"
          : "Missed",
    }))
  );
}

      // ===========================
      // Hours Chart
      // ===========================

      if (hoursRequirement) {
  setHoursChart(
    hoursRequirement.trend.map((item) => ({
      day: item.dayLabel,
      loggedHours: item.loggedHours,
      requiredHours:
        hoursRequirement.hoursRequirementThreshold,
    }))
  );

  setHoursTable(
    hoursRequirement.trend.map((item) => ({
      day: item.dayLabel,
      logged: item.loggedHoursFormatted,
      required: `${hoursRequirement.hoursRequirementThreshold}:00`,
      status:
        item.status === "MET"
          ? "Met"
          : "Missed",
    }))
  );
}

  // ===========================
// Policy Chart
// ===========================

if (policy && policy.length > 0) {
  console.log("Policy API:", policy);

  policy.forEach((item: any) => {
    console.log("Item:", item);
    console.log("Minutes:", item.nonProductiveMinutes);
    console.log("Threshold:", item.threshold);
    console.log("Policy Threshold:", item.policyThreshold);
  });

  setPolicyChart(
    policy.map((item: any) => ({
      day: item.dayLabel,
      minutes: item.nonProductiveMinutes,
      threshold: item.policyThreshold, // change to the correct field
    }))
  );


  setPolicyTable(
    policy.map((item: any) => ({
      day: item.dayLabel,
      safe:
        item.restrictedAppsCount === 0
          ? "Yes"
          : "No",
      violations:
        item.nonProductiveApps || "-",
      status:
        item.status === "MET"
          ? "Met"
          : item.status === "WATCH"
          ? "Pending"
          : "Missed",
    }))
  );
}
            // ===========================
      // Heatmap
      // ===========================

const loginRow: ComplianceHeatmapRow = {
  gate: "Login",
  days: scorecard.map(item => ({
    day: new Date(item.workDate).getDate(),
    workDate: item.workDate,
    status: item.loginStatus === "MET" ? "Met" : "Missed",
  })),
};

const hoursRow: ComplianceHeatmapRow = {
  gate: "Hours",
  days: scorecard.map(item => ({
    day: new Date(item.workDate).getDate(),
    workDate: item.workDate,
    status: item.hoursStatus === "MET" ? "Met" : "Missed",
  })),
};

const policyRow: ComplianceHeatmapRow = {
  gate: "Policy",
  days: scorecard.map(item => ({
    day: new Date(item.workDate).getDate(),
    workDate: item.workDate,
    status: item.policyStatus === "MET" ? "Met" : "Missed",
  })),
};

setHeatmapData({
  period: period,
  subtitle: "Gate-by-gate daily history",
  rows: [
    loginRow,
    hoursRow,
    policyRow,
  ],
});
    } catch (error) {
      console.error(
        "Employee Compliance:",
        error,
      );

      // setError(
      //   "Unable to load compliance data."
      // );
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);


  

useEffect(() => {
  loadCompliance();
}, [fromDate, toDate]);

  

  return {
  header,
  cards,

  loginChart,
  loginTable,

  breakChart,
  breakTable,

  hoursChart,
  hoursTable,

  policyChart,
  policyTable,

  heatmapData,

  loading,

  headerError,
  loginError,
  hoursError,
  policyError,
  heatmapError,

  refresh: loadCompliance,
};
};

export default useEmployeeCompliance;